import { ButtonLink, NavLink } from "@/components/links";
import { StatePage } from "@/components/state-page";
import { formatCurrency, getImageUrl } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { requirePermissions, useHasPermission } from "@/utils/permissions";
import type { APIOutput } from "@/utils/types";
import { Carousel } from "@mantine/carousel";
import { ActionIcon, Anchor, Badge, Card, Modal, Skeleton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useRouter } from "@tanstack/react-router";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import {
  LucideAlertTriangle,
  LucideArrowLeft,
  LucideEdit,
  LucideImage,
  LucideInfo,
  LucideMoreVertical,
  LucideUsers2,
} from "lucide-react";
import { useMemo } from "react";
import * as z from "zod";
import { getTripStatusColor, getTripStatusIcon } from "../-utils";
import { ReservationFiltersSchema } from "../../-reservations/-utils";
import { TripInfo, TripInfoSkeleton } from "./-info";
import TripMenu from "./-menu";
import { TripReservations, TripReservationsSkeleton } from "./-reservation";

export const Route = createFileRoute("/_authenticated/trips/$id/")({
  component: RouteComponent,
  validateSearch: z.object({
    ...ReservationFiltersSchema.shape,
    tab: z.enum(["overview", "reservations"]).optional(),
  }),
  beforeLoad: requirePermissions("trip:get"),
  loaderDeps: ({ search }) => [search.tab] as const,
  loader: ({ context, params, deps: [tab] }) => {
    void context.queryClient.prefetchQuery(orpc.trip.get.queryOptions({ input: { id: params.id } }));
    if (tab === "reservations") {
      void context.queryClient.prefetchQuery(
        orpc.reservation.getAll.queryOptions({
          input: { value: params.id, type: "tripId" },
        }),
      );
    }
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="trip" emptyResourceType="trip" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="trip" emptyResourceType="trip" />,
});

const TABS = [
  {
    id: "overview",
    label: t("overview"),
    icon: LucideInfo,
  },
  {
    id: "reservations",
    label: t("reservations.reservations"),
    icon: LucideUsers2,
  },
] as const;

function RouteComponent() {
  const params = Route.useParams();
  const { tab } = Route.useSearch();

  const { data: trip, error, isLoading, refetch } = useQuery(orpc.trip.get.queryOptions({ input: { id: params.id } }));

  if (isLoading) {
    return <TripSkeleton tab={tab ?? "overview"} />;
  }

  if (error) {
    return <StatePage error={error} reset={refetch} resourceType="trip" emptyResourceType="trip" />;
  }

  if (!trip) {
    return null;
  }

  return <Trip trip={trip} />;
}

function Trip({ trip }: { trip: APIOutput["trip"]["get"] }) {
  const { tab = "overview" } = Route.useSearch();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const hasTripUpdatePermission = useHasPermission("trip:update");

  const [opened, handlers] = useDisclosure(false);

  const imageUrls = useMemo(
    () => trip.imageKeys.map((k) => getImageUrl(k)).filter((o): o is NonNullable<typeof o> => Boolean(o)),
    [trip.imageKeys],
  );

  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        {canGoBack ? (
          <Anchor component="button" onClick={() => router.history.back()} className="flex items-center gap-1.5">
            <LucideArrowLeft className="size-6" />
          </Anchor>
        ) : null}

        <div className="flex items-center gap-1.5">
          {hasTripUpdatePermission ? (
            <ButtonLink
              to="/trips/$id/edit"
              params={{ id: trip.id }}
              size="sm"
              variant="filled"
              leftSection={<LucideEdit className="size-4" />}
            >
              {t("edit")}
            </ButtonLink>
          ) : null}

          <TripMenu
            trip={trip}
            target={
              <ActionIcon size="lg" variant="default" className="size-9">
                <LucideMoreVertical className="size-4" />
              </ActionIcon>
            }
          />
        </div>
      </div>

      {/* Draft Alert */}
      {!trip.visible ? (
        <div className="bg-yellow-light gap-sm p-md flex items-center rounded-md">
          <LucideAlertTriangle className="text-yellow-outline size-5" />
          <p className="translate-y-px text-sm">{t("trips.visibility.draftAlert.description")}</p>
        </div>
      ) : null}

      {/* Main layout - sidebar and content */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar with main trip information */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <MainCard trip={trip} imageUrls={imageUrls} handlers={handlers} />
          <Tabs activeTab={tab} />
        </div>

        {/* Main content area */}
        {tab === "overview" ? <TripInfo trip={trip} /> : <TripReservations trip={trip} />}
      </div>

      {/* Image Carousel Modal */}
      <Modal
        opened={opened}
        onClose={handlers.close}
        size="auto"
        title={t("trips.images.title")}
        classNames={{
          content: "flex flex-col",
          body: "min-h-0 flex grow",
        }}
      >
        <Carousel
          withIndicators
          slideGap="md"
          controlSize={40}
          emblaOptions={{
            loop: true,
          }}
          classNames={{
            viewport: "h-full!",
            container: "h-full!",
          }}
        >
          {imageUrls.map((url) => (
            <Carousel.Slide key={url}>
              <div className="relative h-full">
                <img src={url} alt={trip.name} className="h-full w-full rounded-md object-contain" />
              </div>
            </Carousel.Slide>
          ))}
        </Carousel>
      </Modal>
    </div>
  );
}

function MainCard({
  trip,
  imageUrls,
  handlers,
}: {
  trip: APIOutput["trip"]["get"];
  imageUrls: string[];
  handlers: { open: () => void };
}) {
  return (
    <Card withBorder className="overflow-hidden p-0">
      {/* Hero Image */}
      <div className="relative h-48 w-full">
        {imageUrls.length ? (
          <button type="button" className="group h-full w-full cursor-pointer overflow-hidden" onClick={handlers.open}>
            <img
              src={imageUrls[0]}
              alt={trip.name}
              className="size-full object-cover transition-all duration-300 group-hover:scale-105"
            />
          </button>
        ) : (
          <div className="light:bg-gray-light dark:bg-dark-400 flex size-full items-center justify-center">
            <LucideImage className="text-dimmed size-10" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-black/20 via-black/10 to-transparent select-none" />

        {/* Status Badge */}
        <div className="absolute top-3 right-3 flex gap-1.5">
          <Badge color={getTripStatusColor(trip.status)} leftSection={getTripStatusIcon(trip.status)} size="sm">
            <span className="font-medium capitalize">{t(`trips.status.${trip.status}`)}</span>
          </Badge>
          <Badge color={trip.visible ? "green" : "yellow"} size="sm" variant="filled">
            {t(trip.visible ? "trips.visibility.published" : "trips.visibility.draft")}
          </Badge>
        </div>
      </div>

      {/* Trip Details */}
      <div className="gap-sm p-md flex flex-col">
        <h1 className="text-xl font-semibold">{trip.name}</h1>

        <div className="gap-sm grid grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">{t("trips.id")}</div>
            <div className="text-md font-semibold">{trip.serial}</div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">{t("trips.timeline")}</div>
            <div className="text-md font-semibold">
              {dayjs(trip.startDate).format("DD MMM")} – {dayjs(trip.endDate).format("DD MMM YYYY")}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">{t("trips.capacity.capacity")}</div>
            <div className="text-md font-semibold">{t("person", { count: trip.maxCapacity })}</div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">
              {t(`trips.pricing.${trip.groupPricing.length ? "startingPrice" : "price"}`)}
            </div>
            <div className="text-md text-green-filled font-semibold">{formatCurrency(trip.adultPrice)}</div>
          </div>

          {trip.childMaximumAge ? (
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("trips.pricing.childPrice")}</div>
              <div className="text-md font-semibold">
                {formatCurrency(trip.childPrice)}{" "}
                <span className="text-text text-sm">({t("years_or_younger", { count: trip.childMaximumAge })})</span>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">{t("reservations.reservationFee")}</div>
            <div className="text-md font-semibold">{formatCurrency(trip.adultReservationFee)}</div>
          </div>

          {trip.childMaximumAge ? (
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("reservations.childReservationFee")}</div>
              <div className="text-md font-semibold">
                {formatCurrency(trip.childReservationFee)}{" "}
                <span className="text-text text-sm">({t("years_or_younger", { count: trip.childMaximumAge })})</span>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <div className="text-dimmed text-sm font-medium">{t("reservations.reservationDeadline")}</div>
            <div className="text-md font-semibold">
              {dayjs(trip.reservationDeadline).format(`DD MMM YYYY${t("comma")} hh:mm A`)}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Tabs({ activeTab }: { activeTab: (typeof TABS)[number]["id"] }) {
  return (
    <Card withBorder className="overflow-hidden p-0">
      <div className="flex">
        {/* Vertical Tabs Navigation */}
        <div className="flex w-full flex-col">
          {TABS.map((tab) => (
            <NavLink
              key={tab.id}
              to="."
              replace
              resetScroll={false}
              search={() => ({ tab: tab.id })}
              leftSection={<tab.icon className="size-4" />}
              active={activeTab === tab.id}
              className="not-data-[active=true]:dark:hover:bg-(--mantine-color-dark-5)"
              label={tab.label}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

function TripSkeleton({ tab }: { tab: "overview" | "reservations" }) {
  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        <Skeleton className="size-6 rounded" />
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="size-9 rounded" />
        </div>
      </div>

      {/* Main layout - sidebar and content */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar with main trip information */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          {/* MainCard skeleton */}
          <Card withBorder className="overflow-hidden p-0">
            <Skeleton className="h-48 w-full rounded-b-none" />
            <div className="gap-sm p-md flex flex-col">
              <Skeleton className="h-6 w-3/4" />
              <div className="gap-sm grid grid-cols-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-1.5">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Tabs skeleton */}
          <Card withBorder className="overflow-hidden p-0">
            <div className="flex w-full flex-col">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="px-sm flex h-11 items-center gap-1.5">
                  <Skeleton className="size-5 rounded-full" />
                  <Skeleton className="h-3.5 w-24" />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Main content area */}
        {tab === "overview" ? <TripInfoSkeleton /> : <TripReservationsSkeleton />}
      </div>
    </div>
  );
}
